import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Plus, UserCheck, DollarSign, Calendar, FileText, Settings, Clock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HRPage = () => {
  const { getAuthHeader } = useAuth();
  const [teachers, setTeachers] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [salaries, setSalaries] = useState([]);
  const [stats, setStats] = useState({ present: 0, absent: 0, onLeave: 0, late: 0, totalSalary: 0 });
  const [attendanceDialogOpen, setAttendanceDialogOpen] = useState(false);
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
  const [salaryDialogOpen, setSalaryDialogOpen] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  
  // School Timing Settings
  const [timingSettings, setTimingSettings] = useState({
    school_start_time: '09:00',
    school_end_time: '16:00',
    grace_period_minutes: 15,
    half_day_start_time: '13:00',
    late_to_half_day_count: 3,
    half_day_to_absent_count: 2,
    auto_late_detection: true,
    auto_overtime_detection: true
  });
  
  const [attendanceFormData, setAttendanceFormData] = useState({
    teacher_id: '',
    date: new Date().toISOString().split('T')[0],
    status: 'present',
    remarks: ''
  });
  
  const [leaveFormData, setLeaveFormData] = useState({
    teacher_id: '',
    leave_type: 'casual',
    start_date: '',
    end_date: '',
    reason: '',
    status: 'pending'
  });
  
  const [salaryFormData, setSalaryFormData] = useState({
    teacher_id: '',
    month: new Date().toISOString().slice(0, 7),
    basic_salary: '',
    allowances: '',
    deductions: '',
    remarks: ''
  });

  useEffect(() => {
    fetchData();
    fetchTimingSettings();
  }, []);

  const fetchTimingSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/hr/school-timing`, { headers: getAuthHeader() });
      setTimingSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch timing settings:', error);
    }
  };

  const saveTimingSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.post(`${API_URL}/api/hr/school-timing`, null, {
        params: timingSettings,
        headers: getAuthHeader()
      });
      toast.success('School timing settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const fetchData = async () => {
    try {
      const [teachersRes, attendanceRes, leavesRes, salariesRes] = await Promise.all([
        axios.get(`${API_URL}/api/teachers`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/hr/staff-attendance`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/hr/leaves`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/hr/salaries`, { headers: getAuthHeader() })
      ]);
      
      // Handle paginated response for teachers
      const teachersList = teachersRes.data.items || teachersRes.data || [];
      setTeachers(teachersList);
      setAttendance(attendanceRes.data || []);
      setLeaves(leavesRes.data || []);
      setSalaries(salariesRes.data || []);
      
      const todayAttendance = (attendanceRes.data || []).filter(a => a.date === new Date().toISOString().split('T')[0]);
      const present = todayAttendance.filter(a => a.status === 'present').length;
      const absent = todayAttendance.filter(a => a.status === 'absent').length;
      const late = todayAttendance.filter(a => a.status === 'late').length;
      const onLeave = (leavesRes.data || []).filter(l => l.status === 'approved' && 
        new Date(l.start_date) <= new Date() && new Date(l.end_date) >= new Date()).length;
      const totalSalary = (salariesRes.data || []).reduce((sum, s) => sum + (s.basic_salary + s.allowances - s.deductions), 0);
      
      setStats({ present, absent, onLeave, late, totalSalary });
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load HR data');
    }
  };

  const handleAttendanceSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/hr/staff-attendance`, attendanceFormData, {
        headers: getAuthHeader()
      });
      toast.success('Attendance marked successfully');
      setAttendanceDialogOpen(false);
      setAttendanceFormData({
        teacher_id: '',
        date: new Date().toISOString().split('T')[0],
        status: 'present',
        remarks: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark attendance');
    }
  };

  const handleLeaveSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/hr/leaves`, leaveFormData, {
        headers: getAuthHeader()
      });
      toast.success('Leave request submitted');
      setLeaveDialogOpen(false);
      setLeaveFormData({
        teacher_id: '',
        leave_type: 'casual',
        start_date: '',
        end_date: '',
        reason: '',
        status: 'pending'
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit leave');
    }
  };

  const handleSalarySubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/hr/salaries`, {
        ...salaryFormData,
        basic_salary: parseFloat(salaryFormData.basic_salary),
        allowances: parseFloat(salaryFormData.allowances),
        deductions: parseFloat(salaryFormData.deductions)
      }, { headers: getAuthHeader() });
      toast.success('Salary record added');
      setSalaryDialogOpen(false);
      setSalaryFormData({
        teacher_id: '',
        month: new Date().toISOString().slice(0, 7),
        basic_salary: '',
        allowances: '',
        deductions: '',
        remarks: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add salary');
    }
  };

  const approveLeave = async (leaveId) => {
    try {
      await axios.patch(`${API_URL}/api/hr/leaves/${leaveId}/approve`, {}, {
        headers: getAuthHeader()
      });
      toast.success('Leave approved');
      fetchData();
    } catch (error) {
      toast.error('Failed to approve leave');
    }
  };

  const rejectLeave = async (leaveId) => {
    try {
      await axios.patch(`${API_URL}/api/hr/leaves/${leaveId}/reject`, {}, {
        headers: getAuthHeader()
      });
      toast.success('Leave rejected');
      fetchData();
    } catch (error) {
      toast.error('Failed to reject leave');
    }
  };

  return (
    <DashboardLayout title="HR Management">
      <div className="space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Present Today</CardTitle>
                <div className="bg-green-500 p-2 rounded-lg">
                  <UserCheck className="h-4 w-4 text-white" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">{stats.present}</div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Late Today</CardTitle>
                <div className="bg-amber-500 p-2 rounded-lg">
                  <Clock className="h-4 w-4 text-white" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-amber-600">{stats.late}</div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Absent Today</CardTitle>
                <div className="bg-red-500 p-2 rounded-lg">
                  <UserCheck className="h-4 w-4 text-white" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-red-600">{stats.absent}</div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">On Leave</CardTitle>
                <div className="bg-purple-500 p-2 rounded-lg">
                  <Calendar className="h-4 w-4 text-white" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-purple-600">{stats.onLeave}</div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Salary</CardTitle>
                <div className="bg-blue-500 p-2 rounded-lg">
                  <DollarSign className="h-4 w-4 text-white" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-blue-600">₹{stats.totalSalary.toFixed(2)}</div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2">
          <Dialog open={attendanceDialogOpen} onOpenChange={setAttendanceDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Mark Attendance
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Mark Staff Attendance</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAttendanceSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Teacher *</Label>
                  <Select
                    value={attendanceFormData.teacher_id}
                    onValueChange={(value) => setAttendanceFormData({ ...attendanceFormData, teacher_id: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select teacher" />
                    </SelectTrigger>
                    <SelectContent>
                      {teachers.map(teacher => (
                        <SelectItem key={teacher.id} value={teacher.id}>{teacher.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Date *</Label>
                  <Input
                    type="date"
                    value={attendanceFormData.date}
                    onChange={(e) => setAttendanceFormData({ ...attendanceFormData, date: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Status *</Label>
                  <Select
                    value={attendanceFormData.status}
                    onValueChange={(value) => setAttendanceFormData({ ...attendanceFormData, status: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="present">Present</SelectItem>
                      <SelectItem value="absent">Absent</SelectItem>
                      <SelectItem value="half_day">Half Day</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Remarks</Label>
                  <Textarea
                    value={attendanceFormData.remarks}
                    onChange={(e) => setAttendanceFormData({ ...attendanceFormData, remarks: e.target.value })}
                    placeholder="Optional remarks"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setAttendanceDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit">Mark Attendance</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog open={leaveDialogOpen} onOpenChange={setLeaveDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline">
                <Plus className="h-4 w-4 mr-2" />
                Add Leave
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add Leave Request</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleLeaveSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Teacher *</Label>
                  <Select
                    value={leaveFormData.teacher_id}
                    onValueChange={(value) => setLeaveFormData({ ...leaveFormData, teacher_id: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select teacher" />
                    </SelectTrigger>
                    <SelectContent>
                      {teachers.map(teacher => (
                        <SelectItem key={teacher.id} value={teacher.id}>{teacher.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Leave Type *</Label>
                  <Select
                    value={leaveFormData.leave_type}
                    onValueChange={(value) => setLeaveFormData({ ...leaveFormData, leave_type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="casual">Casual Leave</SelectItem>
                      <SelectItem value="sick">Sick Leave</SelectItem>
                      <SelectItem value="earned">Earned Leave</SelectItem>
                      <SelectItem value="maternity">Maternity Leave</SelectItem>
                      <SelectItem value="unpaid">Unpaid Leave</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Start Date *</Label>
                    <Input
                      type="date"
                      value={leaveFormData.start_date}
                      onChange={(e) => setLeaveFormData({ ...leaveFormData, start_date: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>End Date *</Label>
                    <Input
                      type="date"
                      value={leaveFormData.end_date}
                      onChange={(e) => setLeaveFormData({ ...leaveFormData, end_date: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Reason *</Label>
                  <Textarea
                    value={leaveFormData.reason}
                    onChange={(e) => setLeaveFormData({ ...leaveFormData, reason: e.target.value })}
                    placeholder="Reason for leave"
                    required
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setLeaveDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit">Submit Leave</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog open={salaryDialogOpen} onOpenChange={setSalaryDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline">
                <Plus className="h-4 w-4 mr-2" />
                Add Salary
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add Salary Record</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSalarySubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Teacher *</Label>
                  <Select
                    value={salaryFormData.teacher_id}
                    onValueChange={(value) => setSalaryFormData({ ...salaryFormData, teacher_id: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select teacher" />
                    </SelectTrigger>
                    <SelectContent>
                      {teachers.map(teacher => (
                        <SelectItem key={teacher.id} value={teacher.id}>{teacher.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Month *</Label>
                  <Input
                    type="month"
                    value={salaryFormData.month}
                    onChange={(e) => setSalaryFormData({ ...salaryFormData, month: e.target.value })}
                    required
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Basic Salary *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={salaryFormData.basic_salary}
                      onChange={(e) => setSalaryFormData({ ...salaryFormData, basic_salary: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Allowances</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={salaryFormData.allowances}
                      onChange={(e) => setSalaryFormData({ ...salaryFormData, allowances: e.target.value })}
                      placeholder="0.00"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Deductions</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={salaryFormData.deductions}
                      onChange={(e) => setSalaryFormData({ ...salaryFormData, deductions: e.target.value })}
                      placeholder="0.00"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Remarks</Label>
                  <Textarea
                    value={salaryFormData.remarks}
                    onChange={(e) => setSalaryFormData({ ...salaryFormData, remarks: e.target.value })}
                    placeholder="Optional remarks"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setSalaryDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit">Add Salary</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Tabs */}
        <Card>
          <CardContent className="pt-6">
            <Tabs defaultValue="attendance" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="attendance">Attendance</TabsTrigger>
                <TabsTrigger value="leaves">Leave Requests</TabsTrigger>
                <TabsTrigger value="salary">Salary</TabsTrigger>
              </TabsList>

              <TabsContent value="attendance" className="mt-4">
                {attendance.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No attendance records</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-3 px-4 font-medium">Staff</th>
                          <th className="text-left py-3 px-4 font-medium">Date</th>
                          <th className="text-left py-3 px-4 font-medium">Check In</th>
                          <th className="text-left py-3 px-4 font-medium">Check Out</th>
                          <th className="text-left py-3 px-4 font-medium">Status</th>
                          <th className="text-left py-3 px-4 font-medium">Late (min)</th>
                          <th className="text-left py-3 px-4 font-medium">Overtime (min)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {attendance.map(record => {
                          const teacher = teachers.find(t => t.id === record.teacher_id || t.id === record.staff_id);
                          return (
                            <tr key={record.id} className="border-b hover:bg-muted/50">
                              <td className="py-3 px-4">{record.staff_name || teacher?.name || '-'}</td>
                              <td className="py-3 px-4">{record.date}</td>
                              <td className="py-3 px-4">{record.check_in || '-'}</td>
                              <td className="py-3 px-4">{record.check_out || '-'}</td>
                              <td className="py-3 px-4">
                                <span className={`px-2 py-1 rounded text-xs ${
                                  record.status === 'present' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                                  record.status === 'late' ? 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200' :
                                  record.status === 'half_day' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200' :
                                  record.status === 'absent' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {record.status}
                                </span>
                              </td>
                              <td className="py-3 px-4 text-amber-600">{record.late_minutes || 0}</td>
                              <td className="py-3 px-4 text-green-600">{record.overtime_minutes || 0}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="leaves" className="mt-4">
                {leaves.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No leave requests</p>
                ) : (
                  <div className="space-y-3">
                    {leaves.map(leave => {
                      const teacher = teachers.find(t => t.id === leave.teacher_id);
                      return (
                        <div key={leave.id} className="p-4 border rounded-lg">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h3 className="font-semibold">{teacher?.name || 'Unknown'}</h3>
                              <p className="text-sm text-muted-foreground mt-1">
                                {leave.leave_type.replace('_', ' ')} | {leave.start_date} to {leave.end_date}
                              </p>
                              <p className="text-sm mt-1">{leave.reason}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={`px-2 py-1 rounded text-xs ${
                                leave.status === 'approved' ? 'bg-green-100 text-green-800' :
                                leave.status === 'rejected' ? 'bg-red-100 text-red-800' :
                                'bg-amber-100 text-amber-800'
                              }`}>
                                {leave.status}
                              </span>
                              {leave.status === 'pending' && (
                                <div className="flex gap-2">
                                  <Button size="sm" onClick={() => approveLeave(leave.id)}>Approve</Button>
                                  <Button size="sm" variant="outline" onClick={() => rejectLeave(leave.id)}>Reject</Button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="salary" className="mt-4">
                {salaries.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No salary records</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-3 px-4 font-medium">Teacher</th>
                          <th className="text-left py-3 px-4 font-medium">Month</th>
                          <th className="text-left py-3 px-4 font-medium">Basic</th>
                          <th className="text-left py-3 px-4 font-medium">Allowances</th>
                          <th className="text-left py-3 px-4 font-medium">Deductions</th>
                          <th className="text-left py-3 px-4 font-medium">Net Salary</th>
                        </tr>
                      </thead>
                      <tbody>
                        {salaries.map(salary => {
                          const teacher = teachers.find(t => t.id === salary.teacher_id);
                          const netSalary = salary.basic_salary + salary.allowances - salary.deductions;
                          return (
                            <tr key={salary.id} className="border-b hover:bg-muted/50">
                              <td className="py-3 px-4">{teacher?.name || '-'}</td>
                              <td className="py-3 px-4">{salary.month}</td>
                              <td className="py-3 px-4">₹{salary.basic_salary.toFixed(2)}</td>
                              <td className="py-3 px-4 text-green-600">+₹{salary.allowances.toFixed(2)}</td>
                              <td className="py-3 px-4 text-red-600">-₹{salary.deductions.toFixed(2)}</td>
                              <td className="py-3 px-4 font-semibold">₹{netSalary.toFixed(2)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </TabsContent>
            </Tabs>
            
            {/* Settings Redirect */}
            <div className="mt-4 p-4 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground">
                ⚙️ <strong>Settings:</strong> Attendance & Salary deduction settings are now in{' '}
                <a href="/academic-calendar" className="text-primary hover:underline font-medium">
                  Academic Calendar → Settings
                </a>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default HRPage;